import {IssueRelativeTime} from '@github-ui/ago'
import {isLoggedIn} from '@github-ui/client-env'
import {SpammyBadge} from '@github-ui/commenting/SpammyBadge'
import {type PinnedCommentMutationOptions, usePinnedComment} from '@github-ui/commenting/usePinnedComment'
import {copyText} from '@github-ui/copy-to-clipboard'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import {ProfileReference} from '@github-ui/profile-reference'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {ArrowDownIcon, KebabHorizontalIcon, LinkIcon, PinIcon, PinSlashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link} from '@primer/react'
import {Suspense, useMemo, useRef} from 'react'

import styles from './PinnedCommentDisplay.module.css'

/**
 * Extracts the hash fragment from a comment URL so that navigation stays
 * in-page instead of forcing a full redirect to the issues show route.
 */
function getCommentHash(url?: string): string {
  if (!url) return ''
  const hashIndex = url.indexOf('#')
  return hashIndex !== -1 ? url.slice(hashIndex) : url
}

type IssueViewerIssue = NonNullable<NonNullable<IssueViewerViewQueryQuery['repository']>['issue']>

// RELAY-CLEANUP(IssueBody): remove RelayCompat when IssueBody leaves Relay.
export type PinnedCommentDisplayFragment = RelayCompat<
  Pick<IssueViewerIssue, 'id' | 'pinnedIssueComment'> & {
    readonly repository: Pick<IssueViewerIssue['repository'], 'nameWithOwner'>
  }
>

// RELAY-CLEANUP(IssueBodyViewer): typed to accept data from the still-Relay parent.
// Remove when IssueBodyViewer leaves Relay.
type PinnedCommentDisplaySecondaryFragment = {
  readonly pinnedIssueComment?: NonNullable<
    NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>['pinnedIssueComment']
  > | null
}

export type PinnedCommentDisplayProps = {
  issue: PinnedCommentDisplayFragment
  secondary?: PinnedCommentDisplaySecondaryFragment | null
}

export function PinnedCommentDisplay({issue: data, secondary}: PinnedCommentDisplayProps) {
  if (!data.pinnedIssueComment?.issueComment) {
    return null
  }

  const {issueComment: comment, pinnedBy} = data.pinnedIssueComment
  const showSpammyBadge = secondary?.pinnedIssueComment?.issueComment?.showSpammyBadge ?? false
  const commentHash = getCommentHash(comment.url)

  return (
    <Suspense fallback={null}>
      <div className={styles.PinnedCommentContainer} data-testid="pinned-comment-display">
        <PinnedCommentHeader
          pinnedBy={pinnedBy}
          issueId={data.id}
          nameWithOwner={data.repository.nameWithOwner}
          comment={comment}
          showSpammyBadge={showSpammyBadge}
        />
        {comment.author && <AuthorInfo comment={comment} />}
        <NewMarkdownViewer
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={`${styles.CommentBody} Links--underline`}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to GraphQL fragment PinnedCommentDisplay.shortBodyHTML */
          verifiedHTML={comment.shortBodyHTML as SafeHTMLString}
        />
        <div className={styles.FullCommentLink}>
          <Link href={commentHash}>
            View full comment <ArrowDownIcon size={16} />
          </Link>
        </div>
      </div>
    </Suspense>
  )
}

type PinnedComment = NonNullable<NonNullable<PinnedCommentDisplayFragment['pinnedIssueComment']>['issueComment']>

type PinnedCommentHeaderProps = {
  issueId: string
  nameWithOwner: string
  pinnedBy: {
    login: string
  }
  comment: PinnedComment
  showSpammyBadge: boolean
}

type MenuAction = {
  key: string
  label: string
  icon: React.ComponentType
  onSelect: () => void
  ariaLabel: string
  visible: boolean
}

function PinnedCommentHeader({pinnedBy, issueId, nameWithOwner, comment, showSpammyBadge}: PinnedCommentHeaderProps) {
  const pinnedCommentOptions = useMemo<PinnedCommentMutationOptions>(
    () => ({
      issueId,
      nameWithOwner,
      comment: {
        id: comment.id,
        shortBodyHTML: comment.shortBodyHTML,
        createdAt: comment.createdAt,
        url: comment.url,
        author: comment.author
          ? {
              __typename: comment.author.__typename,
              id: comment.author.id,
              login: comment.author.login,
              avatarUrl: comment.author.avatarUrl,
              profileUrl: comment.author.profileUrl ?? null,
            }
          : null,
      },
    }),
    [comment, issueId, nameWithOwner],
  )
  const {unpinComment} = usePinnedComment(pinnedCommentOptions)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const handleUnpin = () => {
    unpinComment()
  }

  const handleCopyLink = () => {
    copyText(comment.url)
  }

  const menuActions: MenuAction[] = [
    {
      key: 'copy-link',
      label: 'Copy link',
      icon: LinkIcon,
      onSelect: handleCopyLink,
      ariaLabel: 'Copy link to pinned comment',
      visible: true,
    },
    {
      key: 'unpin',
      label: 'Unpin',
      icon: PinSlashIcon,
      onSelect: handleUnpin,
      ariaLabel: 'Unpin comment',
      visible: isLoggedIn() && comment.viewerCanUnpin,
    },
  ]

  const visibleActions = menuActions.filter(action => action.visible)

  return (
    <div className={styles.PinnedCommentHeader}>
      <div className={styles.PinnedByInfo}>
        <PinIcon size={14} className={styles.PinnedByIcon} />
        {pinnedBy && (
          <>
            Pinned by{' '}
            <Link href={`/${pinnedBy.login}`} {...getHovercardAttributesForType('user', {login: pinnedBy.login})}>
              <ProfileReference login={pinnedBy.login} isAgent={false} />
            </Link>
          </>
        )}
      </div>
      <div className={styles.HeaderActions}>
        {showSpammyBadge && <SpammyBadge />}
        {visibleActions.length > 0 && (
          <ActionMenu anchorRef={menuButtonRef}>
            <ActionMenu.Anchor>
              <IconButton
                ref={menuButtonRef}
                size="small"
                icon={KebabHorizontalIcon}
                variant="invisible"
                aria-label="Pinned comment options"
                className={styles.ActionsMenuButton}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="auto">
              <ActionList>
                {visibleActions.map(action => (
                  <ActionList.Item key={action.key} onSelect={action.onSelect} aria-label={action.ariaLabel}>
                    <ActionList.LeadingVisual>
                      <action.icon />
                    </ActionList.LeadingVisual>
                    {action.label}
                  </ActionList.Item>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </div>
    </div>
  )
}

type AuthorInfoProps = {
  comment: PinnedComment
}

function AuthorInfo({comment}: AuthorInfoProps) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const author = comment.author!
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  return (
    <div className={styles.AuthorInfo}>
      <Link
        href={author.profileUrl ?? undefined}
        {...getHovercardAttributesForType('user', {login: author.login})}
        className={styles.AuthorLink}
      >
        <GitHubAvatar className={styles.Avatar} src={author.avatarUrl} size={25} alt={`@${author.login}`} />
        {author.login}
      </Link>
      <Link href={getCommentHash(comment.url)} data-testid="issue-body-header-link" className={styles.TimestampLink}>
        <IssueRelativeTime datetime={comment.createdAt} />
      </Link>
    </div>
  )
}
